import { useQuery, useMutation, useQueryClient } from 'react-query';
import { promptsApi } from '../../../../../api/prompts';

export const useSettings = ( sessionId ) => {
	const { data, isLoading, isError, error, refetch } = useQuery(
		[ 'settings', sessionId ],
		() => promptsApi.getSettings( sessionId ),
		{
			enabled   : !!sessionId, // Only run query if sessionId is provided
			staleTime : 5 * 60 * 1000, // 5 minutes
			cacheTime : 10 * 60 * 1000, // 10 minutes
		}
	);

	return {
		settings: data?.data,
		isLoading,
		isError,
		error,
		refetch,
	};
};

export const useUpdateSettings = () => {
	const queryClient = useQueryClient();

	const { mutate, mutateAsync, isLoading, isError, error, isSuccess } = useMutation(
		promptsApi.updateSettings,
		{
			onSuccess: ( data, variables ) => {
				// Invalidate and refetch settings for the specific session
				queryClient.invalidateQueries( [ 'settings', variables.session_id ] );

				// Optionally update the specific settings in cache
				queryClient.setQueryData( [ 'settings', variables.session_id ], ( oldData ) => {
					if ( !oldData?.data ) return oldData;
					return {
						...oldData,
						data: { ...oldData.data, ...variables }
					};
				} );
			},
			onError: ( error ) => {
				console.error( 'Error updating settings:', error );
			}
		}
	);

	return {
		updateSettings      : mutate,
		updateSettingsAsync : mutateAsync,
		isLoading,
		isError,
		error,
		isSuccess,
	};
};

export const useStatusIA = ( sessionId ) => {
	const { data, isLoading, isError, error, refetch } = useQuery(
		[ 'statusIA', sessionId ],
		() => promptsApi.getStatusIA( sessionId ),
		{
			enabled   : !!sessionId, // Only run query if sessionId is provided
			staleTime : 30 * 1000, // 30 seconds (status might change more frequently)
			cacheTime : 2 * 60 * 1000, // 2 minutes
		}
	);

	return {
		statusIA: data?.data?.is_on,
		shouldFinalizeChat: data?.data?.ia_should_finalize_chats,
		getChatsWithoutResponse: data?.data?.ia_get_chats_without_response,
		shouldSendMessageAfterCall: data?.data?.should_send_message_after_call,
		isLoading,
		isError,
		error,
		refetch,
	};
};

export const useToggleIA = () => {
	const queryClient = useQueryClient();
	const { mutate, mutateAsync, isLoading, isError, error, isSuccess } = useMutation(
		( data ) => promptsApi.toggleIA( data.session_id, { 
			shouldFinalizeChat: data.shouldFinalizeChat,
			getChatsWithoutResponse: data.getChatsWithoutResponse,
			statusIA: data.statusIA,
			shouldSendMessageAfterCall: data.shouldSendMessageAfterCall
		} ),
		{
			onSuccess: ( _response, variables ) => {
				queryClient.invalidateQueries( [ 'statusIA', variables.session_id ] );
				queryClient.invalidateQueries( [ 'settings', variables.session_id ] );
			},
			onError: ( error ) => {
				console.error( 'Error toggling IA:', error );
			}
		}
	);

	return {
		toggleIA      : mutate,
		toggleIAAsync : mutateAsync,
		isLoading,
		isError,
		error,
		isSuccess,
	};
};
